import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Users, Activity, AlertTriangle, Trash2, Eye, Loader2, ArrowLeft, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import TierBadge from '@/components/TierBadge';
import { SentimentPill } from '@/components/CommunityPostBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatTile({ label, value, Icon, tone = 'violet' }) {
  const cls = {
    violet: 'bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  }[tone] || 'bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300';
  return (
    <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cls}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold tabular-nums">{value ?? '—'}</p>
      </div>
    </div>
  );
}

function ReportedPostCard({ post, onDelete, onApprove }) {
  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/20 p-4" data-testid={`reported-post-${post.post_id}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5 flex-wrap">
        <Flag className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        <span className="font-bold text-amber-700 dark:text-amber-400">
          ⚑ {post.report_count} reports
        </span>
        <span>·</span>
        <span className="font-mono font-bold tabular-nums">
          Weight: {post.cumulative_report_weight || 0}
        </span>
        {post.hidden_under_review ? (
          <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 text-[10px] font-bold uppercase">
            Auto-hidden
          </span>
        ) : null}
        <span className="ml-auto text-[11px]">@{post.handle}</span>
      </div>
      <div className="flex items-center gap-2 text-xs flex-wrap mb-1">
        {post.ticker ? (
          <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 text-[10px]">
            {post.ticker}
          </span>
        ) : null}
        <SentimentPill label={post.sentiment_label} score={post.sentiment_score} />
      </div>
      <h3 className="text-sm font-bold mb-1">{post.title}</h3>
      <p className="text-xs text-muted-foreground line-clamp-3 mb-2 whitespace-pre-wrap">{post.body}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => onApprove(post)} data-testid={`reported-approve-${post.post_id}`}>
          <Eye className="w-3.5 h-3.5 mr-1" /> Restore
        </Button>
        <Button size="sm" variant="destructive" onClick={() => onDelete(post)} data-testid={`reported-delete-${post.post_id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

function ReportedCommentCard({ comment, onDelete }) {
  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/20 p-3" data-testid={`reported-comment-${comment.comment_id}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5 flex-wrap">
        <Flag className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        <span className="font-bold text-amber-700 dark:text-amber-400">⚑ {comment.report_count}</span>
        <span>·</span>
        <span className="font-mono tabular-nums">W: {comment.cumulative_report_weight || 0}</span>
        <span className="ml-auto text-[11px]">@{comment.handle}</span>
      </div>
      <p className="text-xs whitespace-pre-wrap mb-2">{comment.body}</p>
      <Button size="sm" variant="destructive" onClick={() => onDelete(comment)} data-testid={`reported-delete-comment-${comment.comment_id}`}>
        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
      </Button>
    </div>
  );
}

export default function AdminCommunityPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        if (r.data?.role === 'admin') setAuthorized(true);
        else { setAuthorized(false); navigate('/'); }
      })
      .catch(() => { setAuthorized(false); navigate('/login'); });
  }, [navigate]);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        axios.get(`${API}/admin/community/member-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/community/reported`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setPosts(q.data.posts || []);
      setComments(q.data.comments || []);
    } catch (e) {
      toast.error('Failed to load moderation queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authorized) reload(); }, [authorized]);

  const handleDeletePost = async (post) => {
    if (!window.confirm(`Delete this post by @${post.handle}? (Hard moderation action — author will be notified)`)) return;
    try {
      await axios.delete(`${API}/community/posts/${post.post_id}`, { withCredentials: true });
      toast.success('Post deleted.');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleApprovePost = async (post) => {
    try {
      await axios.post(`${API}/admin/community/posts/${post.post_id}/restore`, {}, { withCredentials: true });
      toast.success('Post restored — visible in feed again.');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed.');
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!window.confirm(`Delete this comment by @${comment.handle}?`)) return;
    try {
      await axios.delete(`${API}/community/comments/${comment.comment_id}`, { withCredentials: true });
      toast.success('Comment deleted.');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  if (authorized === null) {
    return (
      <div className="min-h-screen min-h-[100svh] flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking permissions…
      </div>
    );
  }
  if (!authorized) return null;

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="admin-community-page">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        <div className="mb-4 flex items-center gap-2">
          <BackOrHomeButton />
        </div>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Community Moderation</h1>
            <p className="text-sm text-muted-foreground">Auto-hidden posts + King-fast-tracked reports. Admin only.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <StatTile label="Total Members" value={stats?.total_members} Icon={Users} tone="violet" />
          <StatTile label="Members Online (15m)" value={stats?.members_online} Icon={Activity} tone="emerald" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading queue…
          </div>
        ) : (
          <div className="space-y-6">
            <section>
              <h2 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Reported posts ({posts.length})
              </h2>
              {posts.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                  Nothing in the queue. The community is behaving itself.
                </div>
              ) : (
                <div className="space-y-3">
                  {posts.map((p) => (
                    <ReportedPostCard
                      key={p.post_id}
                      post={p}
                      onDelete={handleDeletePost}
                      onApprove={handleApprovePost}
                    />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Reported comments ({comments.length})
              </h2>
              {comments.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                  No flagged comments.
                </div>
              ) : (
                <div className="space-y-2">
                  {comments.map((c) => (
                    <ReportedCommentCard key={c.comment_id} comment={c} onDelete={handleDeleteComment} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
